import React, { useContext } from 'react';
import Brand from 'layout/Brand/Brand';
import Navigation, { NavigationLine } from 'layout/Navigation/Navigation';
import User from 'layout/User/User';
import ThemeContext from 'contexts/themeContext';
import Aside, { AsideBody, AsideFoot, AsideHead } from 'layout/Aside/Aside';
import { businessMenu, dashboardMenu, settingsMenu } from 'menu';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'stores/store';
import {
	selectCompanyId,
	selectProjectId,
	setCompanyId,
	setProjectId,
} from 'stores/projectCompanySlice';
import Dropdown, {
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from 'components/bootstrap/Dropdown';
import Button from 'components/bootstrap/Button';
import { checkPermission, getCompany, getProject } from 'helpers/helpers';
import { fetchStaff } from 'stores/authSlice';
import useAuthService, { MethodTypeEnum } from 'helpers/auth_service';
import { useFormik } from 'formik';
import { links } from 'helpers/urls';
import showNotification from 'components/extras/showNotification';
import { SuccessResponseEnum } from 'enums';
import Icon from 'components/icon/Icon';
import classNames from 'classnames';

const DefaultAside = () => {
	const { asideStatus, setAsideStatus } = useContext(ThemeContext);

	const dispatch = useDispatch<AppDispatch>();

	const { user } = useSelector((state: RootState) => state.auth);
	const companyId = useSelector((state: RootState) => selectCompanyId(state));
	const projectId = useSelector((state: RootState) => selectProjectId(state));

	const company = () => getCompany(companyId, user);
	const project = () => getProject(projectId, companyId, user);
	const currentCompany = company();
	const currentProject = project();

	const { bodyRequest } = useAuthService();

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			is_live: !!(currentProject && currentProject.is_live),
		},
		onSubmit: async () => {
			const response = await bodyRequest(
				`${links.changeProjectStatus}/${currentProject?.id}`,
				true,
				{},
				MethodTypeEnum.Put,
			);
			if (response.status) {
				dispatch(fetchStaff());
				showNotification(
					<span className='d-flex align-items-center'>
						<Icon icon='Info' size='lg' className='me-1' />
						<span>{SuccessResponseEnum.UPDATED}</span>
					</span>,
					response.message,
				);
			}
		},
	});

	return (
		<Aside>
			<AsideHead>
				<Brand asideStatus={asideStatus} setAsideStatus={setAsideStatus} />
			</AsideHead>
			<AsideBody>
				{user && user.companies.length > 0 && (
					<Dropdown className='w-100'>
						<DropdownToggle hasIcon>
							<Button
								className='w-100 mb-2'
								icon='Folder2Open'
								color='light'
								isOutline
								data-tour='switch-organization-tour'
								rounded={0}>
								{currentCompany?.name.toUpperCase()}
							</Button>
						</DropdownToggle>
						<DropdownMenu>
							{user.companies.map((item, i: number) => (
								<DropdownItem key={`change-company-active-${item.id}-${i}`}>
									<Button
										onClick={async () => {
											if (item.id !== currentCompany?.id) {
												dispatch(setCompanyId(item.id));
												dispatch(setProjectId(item.projects[0].id));
												dispatch(fetchStaff());
											}
										}}>
										{item.name.toUpperCase()}
									</Button>
								</DropdownItem>
							))}
						</DropdownMenu>
					</Dropdown>
				)}
				<Navigation menu={dashboardMenu} id='aside-dashboard' />
				<NavigationLine />
				<Navigation menu={businessMenu} id='aside-business' />
				<NavigationLine />
				<Navigation menu={settingsMenu} id='aside-settings' />
			</AsideBody>
			<AsideFoot>
				{checkPermission('projects', companyId, user) && currentProject && (
					<nav aria-label='aside-bottom-menu' data-tour='switch-project-tour'>
						<div className='navigation'>
							<div
								role='presentation'
								className='navigation-item cursor-pointer'
								onClick={() => {
									formik.handleSubmit();
								}}
								data-tour='documentation'>
								<span className='navigation-link navigation-link-pill'>
									<span className='navigation-link-info'>
										<Icon
											icon={currentProject.is_live ? 'ToggleOn' : 'ToggleOff'}
											color={currentProject.is_live ? 'success' : 'danger'}
											className='navigation-icon'
										/>
										<span className='navigation-text'>
											{currentProject.is_live ? 'Live Mode' : 'Sandbox Mode'}
										</span>
									</span>
									<span className='navigation-link-extra'>
										<Icon
											icon='Circle'
											className={classNames(
												'navigation-notification',
												'animate__animated animate__heartBeat animate__infinite animate__slower',
												{
													'text-danger': !currentProject.is_live,
													'text-success': currentProject.is_live,
												},
											)}
										/>
									</span>
								</span>
							</div>
						</div>
					</nav>
				)}
				<User />
			</AsideFoot>
		</Aside>
	);
};

export default DefaultAside;
